/**
 * "Is a coaching session live right now?" — a tiny module-level signal for the
 * installed-PWA auto-refresh (VersionAutoRefresh reads it before reloading).
 *
 * Deliberately NOT React state: the version detector lives at the layout root
 * and must read this without prop-drilling through the whole tree. LiveTranscript
 * marks a session active for its mounted lifetime; the detector holds any pending
 * reload until this returns false, so a page refresh never interrupts a live
 * conversation.
 *
 * Ref-counted (not a bare boolean) so overlapping/remounting sessions — e.g. a
 * React 18 StrictMode double-mount in dev — can't leave the flag falsely stuck
 * on or off: each `markSessionActive()` is balanced by exactly one release.
 */
let activeCount = 0;

/**
 * Mark a session active. Returns a release fn (idempotent) to call when it ends
 * — designed to be returned directly from a React effect as its cleanup.
 */
export function markSessionActive(): () => void {
  activeCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeCount = Math.max(0, activeCount - 1);
  };
}

/** True while at least one session is active. */
export function isSessionActive(): boolean {
  return activeCount > 0;
}

/** Test-only: reset the counter between cases. */
export function __resetSessionActivity(): void {
  activeCount = 0;
}
